(() => {
      if (window.__hemiCollectionCardsV2Ready) return;
      window.__hemiCollectionCardsV2Ready = true;

      const variantCache = new WeakMap();
      const productImageCache = new WeakMap();
      const baseColorCache = new WeakMap();
      const imageCache = new Map();
      const warmedCards = new WeakSet();

      const initSlider = (card) => {
        const slider = card.querySelector('.alp-pc6__slider');
        if (!slider || slider.dataset.pc6Init === '1') return;
        const track = slider.querySelector('.alp-pc6__track');
        const previous = slider.querySelector('.alp-pc6__btn-prev');
        const next = slider.querySelector('.alp-pc6__btn-next');
        const drag = card.querySelector('.alp-pc6__scrollbar-drag');
        const initialTotal = Number.parseInt(slider.dataset.count || '0', 10);
        if (!track || initialTotal < 2) return;

        slider.dataset.pc6Init = '1';
        slider.dataset.pc6Current = '0';
        slider.style.touchAction = 'pan-y';
        slider.style.userSelect = 'none';
        slider.addEventListener('dragstart', (event) => event.preventDefault());

        const goTo = (index) => {
          const total = Math.max(1, Number.parseInt(slider.dataset.count || '1', 10));
          const current = ((index % total) + total) % total;
          slider.dataset.pc6Current = String(current);
          track.style.transform = `translate3d(-${current * 100}%, 0, 0)`;
          if (drag) drag.style.width = `${100 / total}%`;
          if (drag) drag.style.transform = `translateX(${current * 100}%)`;
        };
        slider.pc6GoTo = goTo;
        goTo(0);

        previous?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          goTo(Number.parseInt(slider.dataset.pc6Current || '0', 10) - 1);
        });
        next?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          goTo(Number.parseInt(slider.dataset.pc6Current || '0', 10) + 1);
        });

        let pointerId = null;
        let startX = 0;
        let startY = 0;
        let didSwipe = false;

        slider.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse') return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startY = event.clientY;
          didSwipe = false;
          slider.setPointerCapture?.(pointerId);
        }, true);

        slider.addEventListener('pointerup', (event) => {
          if (pointerId !== event.pointerId) return;
          const dx = startX - event.clientX;
          const dy = startY - event.clientY;
          pointerId = null;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
            didSwipe = true;
            goTo(Number.parseInt(slider.dataset.pc6Current || '0', 10) + (dx > 0 ? 1 : -1));
          }
        }, true);

        slider.addEventListener('pointercancel', () => {
          pointerId = null;
        });

        slider.addEventListener('click', (event) => {
          if (!didSwipe) return;
          event.preventDefault();
          event.stopPropagation();
          didSwipe = false;
        }, true);
      };

      const variantsFor = (card) => {
        if (variantCache.has(card)) return variantCache.get(card);
        let variants = [];
        try {
          variants = JSON.parse(card.dataset.variantData || '[]');
        } catch (error) {
          variants = [];
        }
        variantCache.set(card, variants);
        return variants;
      };

      const productImagesFor = (card) => {
        if (productImageCache.has(card)) return productImageCache.get(card);
        let images = [];
        try {
          images = JSON.parse(card.dataset.imageData || '[]');
        } catch (error) {
          images = [];
        }
        productImageCache.set(card, images);
        return images;
      };

      const imageGroupFor = (card, variant) => {
        const images = productImagesFor(card);
        const colorIndex = Number.parseInt(card.dataset.colorOptionIndex || '-1', 10);
        const start = Math.max(0, Number.parseInt(variant?.featured_image?.position || '1', 10) - 1);
        if (!images.length || colorIndex < 0) return images.slice(start, start + 6);

        const colorValue = Array.isArray(variant.options) ? variant.options[colorIndex] : '';
        const groupStarts = new Map();
        variantsFor(card).forEach((item) => {
          const itemColor = Array.isArray(item.options) ? item.options[colorIndex] : '';
          const position = Number.parseInt(item?.featured_image?.position || '0', 10);
          if (!itemColor || !position) return;
          if (!groupStarts.has(itemColor) || position < groupStarts.get(itemColor)) {
            groupStarts.set(itemColor, position);
          }
        });

        const groupStart = Math.max(0, (groupStarts.get(colorValue) || start + 1) - 1);
        let groupEnd = images.length;
        groupStarts.forEach((position, itemColor) => {
          const nextStart = position - 1;
          if (itemColor !== colorValue && nextStart > groupStart && nextStart < groupEnd) {
            groupEnd = nextStart;
          }
        });
        return images.slice(groupStart, groupEnd).slice(0, 6);
      };

      const variantFor = (card, swatch) => {
        const colorIndex = Number.parseInt(card.dataset.colorOptionIndex || '-1', 10);
        const colorValue = swatch.dataset.colorValue;
        if (colorIndex < 0 || !colorValue) return null;
        const matchingVariants = variantsFor(card).filter((variant) => {
          return Array.isArray(variant.options) && variant.options[colorIndex] === colorValue;
        });
        return matchingVariants.find((variant) => variant.available) || matchingVariants[0] || null;
      };

      const baseColorsFor = (card) => {
        if (baseColorCache.has(card)) return baseColorCache.get(card);
        let mapping = {};
        try {
          mapping = JSON.parse(card.dataset.colorBaseMap || '{}');
        } catch (error) {
          mapping = {};
        }
        baseColorCache.set(card, mapping);
        return mapping;
      };

      const inferredBaseColor = (label) => {
        const value = String(label || '').toLowerCase();
        if (/black|noir|charcoal/.test(value)) return 'Black';
        if (/white|ivory|cream|ecru|off[- ]?white/.test(value)) return 'White';
        if (/gray|grey|silver|ash/.test(value)) return 'Gray';
        if (/beige|khaki|sand|oat|camel|taupe/.test(value)) return 'Beige';
        if (/brown|coffee|chocolate|mocha|cocoa|tan/.test(value)) return 'Brown';
        if (/navy/.test(value)) return 'Navy';
        if (/blue|denim|indigo|azure|cyan/.test(value)) return 'Blue';
        if (/red|burgundy|wine|maroon|brick/.test(value)) return 'Red';
        if (/pink|rose|blush|mauve/.test(value)) return 'Pink';
        if (/green|olive|sage|mint/.test(value)) return 'Green';
        if (/yellow|mustard/.test(value)) return 'Yellow';
        if (/orange|rust|terracotta/.test(value)) return 'Orange';
        if (/purple|lilac|lavender|plum/.test(value)) return 'Purple';
        if (/gold|silver|bronze/.test(value)) return 'Metallic';
        return '';
      };

      const selectedBaseColors = () => {
        return new URLSearchParams(window.location.search)
          .getAll('filter.p.m.custom.filter_color')
          .filter(Boolean);
      };

      const variantForBaseColors = (card, baseColors) => {
        const colorIndex = Number.parseInt(card.dataset.colorOptionIndex || '-1', 10);
        if (colorIndex < 0 || !baseColors.length) return null;
        const mapping = baseColorsFor(card);
        return variantsFor(card).find((variant) => {
          if (!variant.available) return false;
          const label = Array.isArray(variant.options) ? variant.options[colorIndex] : '';
          const baseValues = Array.isArray(mapping[label]) ? mapping[label] : [];
          const inferred = inferredBaseColor(label);
          return baseColors.some((baseColor) => baseValues.includes(baseColor) || inferred === baseColor);
        }) || null;
      };

      const variantSource = (variant) => {
        return variant?.featured_image?.src || variant?.featured_media?.preview_image?.src || '';
      };

      const imageUrl = (source, width) => {
        if (!source) return '';
        try {
          const url = new URL(source, window.location.href);
          url.searchParams.set('width', String(width));
          return url.toString();
        } catch (error) {
          return source;
        }
      };

      const updateLinks = (card, variantId) => {
        card.querySelectorAll('[data-pc6-card-link]').forEach((link) => {
          try {
            const url = new URL(link.href, window.location.href);
            url.searchParams.set('variant', String(variantId));
            link.href = url.toString();
          } catch (error) {
            const href = link.getAttribute('href') || '';
            link.href = `${href}${href.includes('?') ? '&' : '?'}variant=${variantId}`;
          }
        });
      };

      const imageFor = (card) => {
        return card.querySelector('.alp-pc6__slide:first-child img, .alp-pc6__single-link img');
      };

      const applyImageGroup = (card, variant, colorValue) => {
        const slider = card.querySelector('.alp-pc6__slider');
        const slides = slider ? Array.from(slider.querySelectorAll('.alp-pc6__slide')) : [];
        const group = imageGroupFor(card, variant);
        if (!slider || !slides.length || !group.length) return false;

        slides.forEach((slide, index) => {
          const imageData = group[index];
          slide.hidden = !imageData;
          if (!imageData) return;
          const image = slide.querySelector('img');
          if (!image) return;
          const sources = sourcesFor(imageData.src);
          image.decoding = 'async';
          image.loading = index === 0 ? 'eager' : 'lazy';
          image.sizes = window.innerWidth <= 767 ? '50vw' : '25vw';
          image.srcset = sources.srcset;
          image.src = sources.src;
          image.alt = imageData.alt || `${card.querySelector('[data-js-product-title]')?.textContent?.trim() || ''} ${colorValue || ''}`.trim();
        });

        slider.dataset.count = String(Math.min(group.length, slides.length));
        slider.pc6GoTo?.(0);
        return true;
      };

      const sourcesFor = (source) => {
        const widths = [320, 440, 600, 800, 1000, 1200];
        const primaryWidth = window.innerWidth <= 767 ? 440 : 800;
        return {
          src: imageUrl(source, primaryWidth),
          srcset: widths.map((width) => `${imageUrl(source, width)} ${width}w`).join(', ')
        };
      };

      const preload = (source, priority = 'low') => {
        if (!source) return Promise.resolve(null);
        const sources = sourcesFor(source);
        if (imageCache.has(sources.src)) return imageCache.get(sources.src);
        const request = new Promise((resolve) => {
          const image = new Image();
          image.decoding = 'async';
          image.sizes = window.innerWidth <= 767 ? '50vw' : '25vw';
          if ('fetchPriority' in image) image.fetchPriority = priority;
          image.onload = () => {
            if (typeof image.decode === 'function') {
              image.decode().catch(() => {}).finally(() => resolve(sources));
            } else {
              resolve(sources);
            }
          };
          image.onerror = () => resolve(null);
          image.srcset = sources.srcset;
          image.src = sources.src;
        });
        imageCache.set(sources.src, request);
        return request;
      };

      const applyVariantData = (card, variant, colorValue, swatch = null) => {
        if (!variant) return false;

        card.querySelectorAll('[data-pc6-swatch]').forEach((item) => {
          const selected = swatch ? item === swatch : item.dataset.colorValue === colorValue;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });

        card.dataset.activeVariantId = String(variant.id);
        updateLinks(card, variant.id);
        const source = variantSource(variant);
        const image = imageFor(card);
        const imageGroupApplied = applyImageGroup(card, variant, colorValue);
        if (!imageGroupApplied && source && image) {
          image.classList.add('is-changing');
          preload(source, 'high').then((sources) => {
            if (!sources || card.dataset.activeVariantId !== String(variant.id)) {
              image.classList.remove('is-changing');
              return;
            }
            image.decoding = 'async';
            image.sizes = window.innerWidth <= 767 ? '50vw' : '25vw';
            image.srcset = sources.srcset;
            image.src = sources.src;
            image.alt = `${card.querySelector('[data-js-product-title]')?.textContent?.trim() || ''} ${colorValue || ''}`.trim();
            requestAnimationFrame(() => image.classList.remove('is-changing'));
          });
        }

        const slider = card.querySelector('.alp-pc6__slider');
        if (slider) slider.pc6GoTo?.(0);
        return true;
      };

      const applyVariant = (swatch) => {
        const card = swatch.closest('[data-pc6-card]');
        if (!card) return false;
        return applyVariantData(card, variantFor(card, swatch), swatch.dataset.colorValue, swatch);
      };

      const applyFilteredColor = (card) => {
        const baseColors = selectedBaseColors();
        const gridItem = card.closest('.collection-grid-item');
        if (!baseColors.length) {
          if (gridItem) gridItem.hidden = false;
          return;
        }
        const variant = variantForBaseColors(card, baseColors);
        if (gridItem) gridItem.hidden = !variant;
        if (!variant) return;
        const colorIndex = Number.parseInt(card.dataset.colorOptionIndex || '-1', 10);
        const colorValue = Array.isArray(variant.options) ? variant.options[colorIndex] : '';
        applyVariantData(card, variant, colorValue);
      };

      document.addEventListener('click', (event) => {
        const swatch = event.target?.closest?.('[data-pc6-swatch]');
        if (!swatch || !applyVariant(swatch)) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);

      const warm = (event) => {
        const swatch = event.target?.closest?.('[data-pc6-swatch]');
        if (!swatch) return;
        const card = swatch.closest('[data-pc6-card]');
        if (!card) return;
        preload(variantSource(variantFor(card, swatch)), 'high');
      };

      document.addEventListener('pointerover', warm, { capture: true, passive: true });
      document.addEventListener('touchstart', warm, { capture: true, passive: true });
      document.addEventListener('focusin', warm, true);

      document.addEventListener('pointerdown', (event) => {
        const slider = event.target?.closest?.('.alp-pc6__slider');
        const card = slider?.closest('[data-pc6-card]');
        if (card) initSlider(card);
      }, true);

      document.addEventListener('click', (event) => {
        const arrow = event.target?.closest?.('.alp-pc6__btn-prev, .alp-pc6__btn-next');
        if (!arrow) return;
        const slider = arrow.closest('.alp-pc6__slider');
        const card = slider?.closest('[data-pc6-card]');
        if (!slider || !card) return;
        initSlider(card);
        event.preventDefault();
        event.stopImmediatePropagation();
        const current = Number.parseInt(slider.dataset.pc6Current || '0', 10);
        slider.pc6GoTo?.(current + (arrow.classList.contains('alp-pc6__btn-next') ? 1 : -1));
      }, true);

      const warmCard = (card) => {
        if (!card || warmedCards.has(card)) return;
        warmedCards.add(card);
        card.querySelectorAll('[data-pc6-swatch]').forEach((swatch, index) => {
          const run = () => preload(variantSource(variantFor(card, swatch)), index === 0 ? 'high' : 'low');
          if ('requestIdleCallback' in window) {
            requestIdleCallback(run, { timeout: 800 + (index * 150) });
          } else {
            setTimeout(run, 120 + (index * 100));
          }
        });
      };

      let observer = null;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            warmCard(entry.target);
            observer.unobserve(entry.target);
          });
        }, { rootMargin: '400px 0px' });
      }

      const observeCards = (root = document) => {
        const cards = root.querySelectorAll ? root.querySelectorAll('[data-pc6-card]') : [];
        cards.forEach((card) => {
          initSlider(card);
          applyFilteredColor(card);
          if (observer) {
            observer.observe(card);
          } else {
            warmCard(card);
          }
        });
      };

      const initializeWarmup = () => {
        observeCards();
        const mutationObserver = new MutationObserver((records) => {
          records.forEach((record) => {
            record.addedNodes.forEach((node) => {
              if (!(node instanceof Element)) return;
              if (node.matches('[data-pc6-card]')) {
                initSlider(node);
                applyFilteredColor(node);
                if (observer) observer.observe(node);
                else warmCard(node);
              }
              observeCards(node);
            });
          });
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeWarmup, { once: true });
      } else {
        initializeWarmup();
      }
    })();
  